import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../../../firebase/config'; 
import { collection, query, doc, deleteDoc, onSnapshot, orderBy } from 'firebase/firestore';

import ModalRefacturacion from '../../../ui/ModalRefacturacion'; 
import ModalNotificacion from '../../../ui/ModalNotificacion'; 
import ModalConfirmacion from '../../../ui/ModalConfirmacion'; 
import { getRefacturacionStatusChip, getPersonaChipClass } from '../../../../utils/styles'; 

// --- HELPER DE PARSEO SEGURO ---
const safeParseFloat = (val) => {
    if (val === undefined || val === null) return 0;
    if (typeof val === 'number') return val;
    
    const str = String(val).trim();
    if (!str) return 0;

    if (str.includes(',')) {
        const clean = str.replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
        const num = parseFloat(clean);
        return isNaN(num) ? 0 : num;
    }
    
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
};

// --- ICONOS ---
const IconWallet = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>;

/**
 * RefacturacionesTab - Gestión de movimientos de caución con sincronización en tiempo real.
 */
const RefacturacionesTab = ({ poliza, client }) => {
  const [refacturaciones, setRefacturaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deudaTotal, setDeudaTotal] = useState({ amount: 0, currency: '$' });

  const [showModal, setShowModal] = useState(false);
  const [editingRefacturacion, setEditingRefacturacion] = useState(null);

  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [deletionConfirm, setDeletionConfirm] = useState({ show: false, itemToDelete: null });

  const polizaId = poliza.id;
  const clientId = client.id;

  // --- 1. CÁLCULO DE DEUDA CORREGIDO ---
  const calcularDeudaPendiente = useCallback((lista) => {
      let total = 0;
      // Estados que significan "Movimiento finalizado que no genera saldo"
      const estadosExcluidos = ['ABONADO', 'ABONADO - FALTA RECIBO', 'DEBITADA'];
      let moneda = poliza?.moneda || '$';

      lista.forEach(r => {
          const estado = (r.estadoRefacturacion || '').toUpperCase().trim();
          const importe = safeParseFloat(r.primaEmitida || r.prima);
          const esAnulacion = r.tipoEndoso === 'Anulación';
          
          // LÓGICA DE BALANCE:
          // 1. Si está pagado o es incobrable, no suma ni resta.
          if (estadosExcluidos.includes(estado)) return;

          // 2. Si el estado es ANULADA: 
          // Solo lo sumamos si es un registro de tipo "Anulación" (crédito), 
          // porque este registro es el que cancela la deuda original que sigue figurando como pendiente.
          if (estado === 'ANULADA') {
              if (esAnulacion) {
                  total += (importe > 0 ? -importe : importe);
              }
              // Si es una Refacturación normal ANULADA, simplemente no suma deuda.
              return;
          }

          // 3. Casos estándar (Pendiente, Reclamada, etc.)
          if (esAnulacion && importe > 0) {
              total -= importe;
          } else {
              total += importe;
          }
      });

      // Redondeo de precisión para evitar decimales flotantes infinitos de JS
      const totalRedondeado = Math.round((total + Number.EPSILON) * 100) / 100;
      setDeudaTotal({ amount: totalRedondeado, currency: moneda });
  }, [poliza?.moneda]);

  // --- 2. LISTENER EN TIEMPO REAL (onSnapshot) ---
  useEffect(() => {
    if (!polizaId || !clientId) return;

    setLoading(true);
    const endososRef = collection(db, `public/data/clientes/${clientId}/polizas/${polizaId}/endosos`);
    
    const unsubscribe = onSnapshot(endososRef, (snapshot) => {
        const allDocs = snapshot.docs.map(d => {
            const data = d.data();
            const estadoRaw = data.estadoRefacturacion || data.estadoGestionRenovacion || 'Pendiente';
            return { 
                id: d.id, 
                ...data,
                estadoRefacturacion: estadoRaw.trim() 
            };
        });
        
        const filtered = allDocs
            .filter(e => e.tipoGestion === 'Refacturacion')
            .sort((a, b) => {
                const endosoA = Number(a.numeroEndoso) || 0;
                const endosoB = Number(b.numeroEndoso) || 0;
                return endosoB - endosoA; 
            });

        setRefacturaciones(filtered);
        calcularDeudaPendiente(filtered);
        setLoading(false);
    }, (err) => {
        console.error("Error en listener de refacturaciones:", err);
        setNotification({ show: true, message: 'Error al sincronizar movimientos.', type: 'error' });
        setLoading(false);
    });

    return () => unsubscribe();
  }, [polizaId, clientId, calcularDeudaPendiente]);

  const formatCurrencyDisplay = (currency, amount) => {
    const numericAmount = safeParseFloat(amount);
    if (numericAmount === 0 && (amount === undefined || amount === null || amount === '')) return '-';
    
    const isNegative = numericAmount < 0;
    const amountStr = new Intl.NumberFormat('es-AR', { 
        minimumFractionDigits: 2,
        maximumFractionDigits: 2 
    }).format(Math.abs(numericAmount));
    
    return (
        <span className={`${isNegative ? 'text-red-600' : 'text-blue-700'} font-black tabular-nums whitespace-nowrap`}>
            <span className="text-[10px] mr-px opacity-70 font-bold">{currency || '$'}</span>
            {isNegative ? '-' : ''}{amountStr}
        </span>
    );
  };

  const handleModalSuccess = (msg, type = 'success') => {
      setEditingRefacturacion(null);
      setShowModal(false);
      setNotification({ show: true, message: msg, type: type }); 
  };

  const confirmDelete = (item) => setDeletionConfirm({ show: true, itemToDelete: item });
  
  const handleDelete = async () => {
    const refacturacionId = deletionConfirm.itemToDelete.id;
    setDeletionConfirm({ show: false, itemToDelete: null });

    try {
        const docRef = doc(db, `public/data/clientes/${clientId}/polizas/${polizaId}/endosos`, refacturacionId);
        await deleteDoc(docRef);
        setNotification({ show: true, message: 'Refacturación eliminada correctamente.', type: 'success' });
    } catch (error) {
        console.error("Error al eliminar:", error);
        setNotification({ show: true, message: 'Ocurrió un error al intentar eliminar el registro.', type: 'error' });
    }
  };

  const formatDate = (dateValue) => {
      if(!dateValue) return '-';
      if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
          return new Date(dateValue.seconds * 1000).toLocaleDateString('es-AR');
      }
      if (typeof dateValue === 'string') {
          if (dateValue.includes('/') && dateValue.split('/').length === 3) return dateValue;
          const cleanDate = dateValue.split('T')[0];
          if (cleanDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
              const parts = cleanDate.split('-');
              return `${parts[2]}/${parts[1]}/${parts[0]}`;
          }
      }
      return String(dateValue);
  };

  return (
    <div className="space-y-6 animate-fade-in font-sans text-left pb-10">
        
        {/* HEADER DE DEUDA DINÁMICO */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-between items-center shadow-sm relative overflow-hidden shrink-0">
            <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${deudaTotal.amount > 0 ? 'bg-red-500' : 'bg-emerald-500'}`}></div>
            <div>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${deudaTotal.amount > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                    {deudaTotal.amount >= 0 ? 'Total Refacturaciones Pendientes' : 'Saldo Neto a Favor del Cliente'}
                </p>
                <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-black text-slate-900 tracking-tighter">
                        {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(deudaTotal.amount)}
                    </span>
                    <span className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">{poliza?.moneda || 'ARS'}</span>
                </div>
            </div>

            <button 
                onClick={() => { setEditingRefacturacion(null); setShowModal(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 shadow-lg uppercase tracking-widest active:scale-95"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
                Agregar Refacturación
            </button>
        </div>

        {/* TABLA DE REFACTURACIONES */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse text-[13px] min-w-[1500px]">
                <thead className="bg-slate-900 text-white uppercase font-bold text-[9px] tracking-widest">
                    <tr>
                        <th className="p-4 w-28 whitespace-nowrap">Fecha Carga</th>
                        <th className="p-4 w-28 whitespace-nowrap">Inicio Vig.</th>
                        <th className="p-4 w-28 whitespace-nowrap">Fin Vig.</th>
                        <th className="p-4 w-32 text-center">Tipo de Endoso</th>
                        <th className="p-4 w-28 text-center">Endoso</th>
                        <th className="p-4 w-32">Factura</th>
                        <th className="p-4 w-40 text-right">Importe</th>
                        <th className="p-4 w-32 text-center">Estado</th>
                        <th className="p-4 w-40 text-center">Responsable</th>
                        <th className="p-4 w-28 text-center whitespace-nowrap">Reclamo</th>
                        <th className="p-4 w-32 text-center">Despacho</th>
                        <th className="p-4 w-32 text-center">Quién Cobró</th>
                        <th className="p-4 w-28 text-center whitespace-nowrap">F. Cobro</th>
                        <th className="p-4 w-24 text-right pr-8 sticky right-0 bg-slate-900 shadow-[-5px_0_10px_rgba(0,0,0,0.3)]">Acciones</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {loading ? (
                        <tr><td colSpan="14" className="p-16 text-center text-slate-400 font-bold animate-pulse uppercase tracking-widest">Sincronizando movimientos...</td></tr>
                    ) : refacturaciones.length === 0 ? (
                        <tr><td colSpan="14" className="p-16 text-center text-slate-400 italic font-medium">No hay refacturaciones registradas.</td></tr>
                    ) : (
                        refacturaciones.map(ref => (
                            <tr key={ref.id} className="hover:bg-slate-50 transition-colors group">
                                <td className="p-4 font-bold text-slate-400 tabular-nums whitespace-nowrap">{formatDate(ref.fechaCarga || ref.fechaCreacion)}</td>
                                <td className="p-4 font-bold text-slate-800 tabular-nums whitespace-nowrap">{formatDate(ref.inicioVigencia)}</td>
                                <td className="p-4 font-bold text-slate-800 tabular-nums whitespace-nowrap">{formatDate(ref.finVigencia)}</td>
                                <td className="p-4 text-center">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                                        ref.tipoEndoso === 'Anulación' ? 'bg-red-50 text-red-600 border-red-100' :
                                        ref.tipoEndoso === 'Endoso de Reducción' ? 'bg-orange-50 text-orange-600 border-orange-100' :
                                        ref.tipoEndoso === 'Endoso de Aumento' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                                        'bg-blue-50 text-blue-600 border-blue-100'
                                    }`}>
                                        {ref.tipoEndoso || 'Refacturación'}
                                    </span>
                                </td>
                                <td className="p-4 text-center">
                                    <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded font-black text-[11px] border border-indigo-100 uppercase tracking-tighter shadow-sm">E-{ref.numeroEndoso || '0'}</span>
                                </td>
                                <td className="p-4 font-mono font-bold text-slate-400">{ref.numeroFactura || '-'}</td>
                                <td className="p-4 text-right">
                                    {formatCurrencyDisplay(ref.moneda, ref.primaEmitida || ref.prima)}
                                </td>
                                <td className="p-4 text-center whitespace-nowrap">
                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border ${getRefacturacionStatusChip(ref.estadoRefacturacion)}`}>
                                        {ref.estadoRefacturacion}
                                    </span>
                                </td>
                                <td className="p-4 text-center whitespace-nowrap">
                                    {ref.quienGestiono ? (
                                        <div className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide inline-block ${getPersonaChipClass(ref.quienGestiono)}`}>
                                            {ref.quienGestiono}
                                        </div>
                                    ) : '-'}
                                </td>
                                <td className="p-4 text-center text-slate-500 font-medium tabular-nums whitespace-nowrap">{formatDate(ref.fechaReclamo)}</td>
                                <td className="p-4 text-center">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-bold text-slate-800 uppercase">{ref.formaDespacho || '-'}</span>
                                        <span className="text-[10px] text-slate-500 font-bold mt-0.5">{formatDate(ref.fechaDespacho)}</span>
                                    </div>
                                </td>
                                <td className="p-4 text-center whitespace-nowrap">
                                    {ref.quienCobro ? (
                                        <div className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide inline-block ${getPersonaChipClass(ref.quienCobro)}`}>
                                            {ref.quienCobro}
                                        </div>
                                    ) : '-'}
                                </td>
                                <td className="p-4 text-center text-slate-500 font-medium tabular-nums whitespace-nowrap">{formatDate(ref.fechaCobro)}</td>
                                <td className="p-4 text-right pr-6 sticky right-0 z-10 bg-white group-hover:bg-slate-50 transition-colors shadow-[-5px_0_10px_rgba(0,0,0,0.02)]">
                                    <div className="flex justify-end gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => { setEditingRefacturacion(ref); setShowModal(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100 transition-all"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                        <button onClick={() => confirmDelete(ref)} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100 transition-all"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>

        <style>{`
            .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>

        {/* MODALES */}
        <ModalRefacturacion show={showModal} onClose={() => setShowModal(false)} onSuccess={handleModalSuccess} poliza={poliza} clientData={client} initialData={editingRefacturacion} />
        <ModalNotificacion show={notification.show} onClose={() => setNotification({ show: false, message: '', type: 'success' })} message={notification.message} type={notification.type} />
        <ModalConfirmacion show={deletionConfirm.show} onClose={() => setDeletionConfirm({ show: false, itemToDelete: null })} onConfirm={handleDelete} title="Eliminar Registro" message="¿Estás seguro de que deseas eliminar esta refacturación? Esta acción es irreversible." />
    </div>
  );
};

export default RefacturacionesTab;